import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { WorkByModifiedBy } from 'src/assets/config/dataConfig';
import { RootState } from 'src/redux/store';
import { WorkByRecordRequest, WorkByRecordResponse, WorkByHoursClient, WorkByLineRequest, WorkByLineEntryRequest, WorkByLineResponse, ContractWorkByResponse, ContractWorkBysClient } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { setToNewZealandTimeZone } from 'src/utility/helper/dateHelper';
import { handleError } from 'src/utility/utils';

export interface WorkByRecord extends WorkByRecordResponse { }

export interface WorkByRecordModel extends WorkByRecordRequest {
}

export interface State {
    isLoading: boolean;
    isLoadingContractWorkBy: boolean;
    error: boolean;
    notFound: boolean;
    selectedWorkerContractWorkByList: ContractWorkByResponse[];
    workByRecord: WorkByRecord;
}

// Todo: avoid Date object in state instead use .toISOString();
const initialState: State = {
    isLoading: false,
    isLoadingContractWorkBy: false,
    error: false,
    notFound: false,
    selectedWorkerContractWorkByList: [],
    workByRecord: {
        workByRecordId: 0,
        vesselWorkByName: "default",
        vesselId: 0,
        startDate: new Date(),
        endDate: new Date(),
        workByStatusId: 0,
        notes: '',
        lastModifiedBy: null,
        lastModifiedDate: new Date(),
        isDeleted: false,
        workByLines: []
    },
};

const workByRecordSlice = createSlice({
    name: 'workByDetails',
    initialState,
    reducers: {
        startLoading: (state) => {
            state.isLoading = true;
        },
        startLoadingContractWorkBy: (state) => {
            state.isLoadingContractWorkBy = true;
        },
        hasError: (state, action) => {
            state.error = action.payload;
            state.notFound = false;
            state.isLoading = false;
        },
        getWorkByDetailsSuccess: (state, action) => {
            state.workByRecord = action.payload;
            state.isLoading = false;
        },
        createWorkByLineSuccess:(state, action: PayloadAction<WorkByLineResponse>) => {
            state.workByRecord.workByLines.push(action.payload);
            state.isLoading = false;
        },
        updateWorkByLineSuccess: (state, action: PayloadAction<WorkByRecordResponse>) => {
            state.workByRecord = action.payload;
            state.isLoading = false;
        },
        getWorkerContractWorkByListSuccess: (state, action: PayloadAction<ContractWorkByResponse[]>) => {
            state.selectedWorkerContractWorkByList = action.payload;
            state.isLoadingContractWorkBy = false;
        },
        clearWorkerContractWorkByList: (state, action) => {
            state.selectedWorkerContractWorkByList = [];
        },
        deleteWorkByLineSuccess: (state, action: PayloadAction<number>) => {
            if(state.workByRecord){
                state.workByRecord.workByLines = state.workByRecord.workByLines.filter(line => line.workByLineId !== action.payload);
            }
            state.isLoading = false;
        }
    }
});
// Reducer
export default workByRecordSlice.reducer

// Actions
export const {
    startLoading,
    hasError,
    getWorkByDetailsSuccess,
    createWorkByLineSuccess,
    updateWorkByLineSuccess,
    clearWorkerContractWorkByList,
    deleteWorkByLineSuccess,
} = workByRecordSlice.actions;

// for useSelector
export const selectWorkByRecord = (state: RootState) => state.workByRecord as State;

// ----------------------------------------------------------------------
export function getWorkByRecordById(id: number) {
    return async (dispatch) => {
        dispatch(workByRecordSlice.actions.startLoading());
        try {
            const client = new WorkByHoursClient(undefined, axios);


            const response = await client.getRecordById(id, true, false);

            dispatch(workByRecordSlice.actions.getWorkByDetailsSuccess(response));
            return response;

        } catch (error) {
            dispatch(workByRecordSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function updateWorkByLine(record: WorkByRecord, showNotification: boolean = true) {
    return async (dispatch, getState) => {
        dispatch(workByRecordSlice.actions.startLoading());
        try {
            const request: WorkByRecordRequest = mapResponseToRequest(record);
            request.startDate = setToNewZealandTimeZone(request.startDate);
            request.endDate = setToNewZealandTimeZone(request.endDate);
            const client = new WorkByHoursClient(undefined, axios);
            const response = await client.updateRecord(request);
            dispatch(workByRecordSlice.actions.updateWorkByLineSuccess(response));

            const workByRecordName = getState().workByRecord.workByRecord?.vesselWorkByName || '';
            if (showNotification) {
                toast.success(`${workByRecordName}'s record updated successfully!`);
            }
        } catch (error) {
            dispatch(workByRecordSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function getContractWorkByForSelectedWorker(vesselId: number, workerId: number, date: Date ){
    return async (dispatch) => {
        dispatch(workByRecordSlice.actions.startLoadingContractWorkBy());
        try {
            const client = new WorkByHoursClient(undefined, axios);
            const response = await client.getWorkerContractWorkByList(workerId, vesselId, date);
            dispatch(workByRecordSlice.actions.getWorkerContractWorkByListSuccess(response));
            return response;

        } catch (error) {
            dispatch(workByRecordSlice.actions.hasError(true));
            handleError(error);
        }
    }
}

export function deleteWorkByLine(lineId: number){
    return async (dispatch) => {
        dispatch(workByRecordSlice.actions.startLoading());
        try {
            const client = new WorkByHoursClient(undefined, axios);
            await client.deleteWorkByLine(lineId);

            dispatch(workByRecordSlice.actions.deleteWorkByLineSuccess(lineId));
            toast.success("Work by line deleted successfully!");
            return true;

        } catch (error: any) {
            dispatch(workByRecordSlice.actions.hasError(true));
            handleError(error);
            return false;
        }
    };
}

function mapResponseToRequest(workBy: WorkByRecordResponse) : WorkByRecordRequest{
    const request: WorkByRecordRequest = {
        workByRecordId: workBy.workByRecordId,
        vesselWorkByName: workBy.vesselWorkByName,
        vesselId: workBy.vesselId,
        startDate: workBy.startDate,
        endDate: workBy.endDate,
        workByStatusId: workBy.workByStatusId,
        notes: workBy.notes ?? '',
        modifiedBy: workBy.lastModifiedBy ?? WorkByModifiedBy,
        isDeleted: workBy.isDeleted,
        lines: []
    };
    const reqLines: WorkByLineRequest[] = [];
    workBy.workByLines.forEach((resLine) => {
        const entries: WorkByLineEntryRequest[] = [];
        resLine.workByLineEntries.forEach((resLineEntry) => {
            const entry: WorkByLineEntryRequest = resLineEntry as WorkByLineEntryRequest;
            entries.push(entry);
        });
        const reqLine: WorkByLineRequest = {...resLine, modifiedBy: resLine.lastModifiedBy ?? WorkByModifiedBy, entries: entries
        } as WorkByLineRequest
        reqLines.push(reqLine);
    });
    request.lines = reqLines;

    return request;
}
